// src/views/Settings/StatusCodes/StatusCodesView.tsx
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CodeIcon from '@mui/icons-material/Code';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { AddStatusCodeModal } from './AddStatusCodeModal';
import { EditStatusCodeModal } from './EditStatusCodeModal';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { type GridColDef } from '@mui/x-data-grid';
import StatusCodesAPI from 'api/status-codes.api';
import {
  type StatusCodeRecord,
  type CreateStatusCodePayload,
  type UpdateStatusCodePayload,
  type MakeModelRecord,
} from './types';

const StatusCodesContent = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const api = useRef(new StatusCodesAPI()).current;

  const [records, setRecords] = useState<StatusCodeRecord[]>([]);
  const [makeModelOptions, setMakeModelOptions] = useState<MakeModelRecord[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<StatusCodeRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    code: number;
    description: string;
  } | null>(null);

  const toast = (message: string, type: 'success' | 'error' | 'warning') => {
    dispatch(showNotification({ message, type }));
  };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAll();
      setRecords(data);
    } catch {
      toast(t('sc_toast_fetch_failed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  const fetchMakeModels = useCallback(async () => {
    try {
      const data = await api.getMakeModels();
      setMakeModelOptions(data);
    } catch {
      // non-critical; autocomplete just shows empty
    }
  }, [api]);

  useEffect(() => {
    void fetchRecords();
    void fetchMakeModels();
  }, [fetchRecords, fetchMakeModels]);

  const handleAdd = async (payload: CreateStatusCodePayload): Promise<void> => {
    try {
      const result = await api.create(payload);
      await fetchRecords();
      setAddOpen(false);
      toast(t('sc_toast_created'), 'success');
      if (result.failed.length > 0) {
        const pairs = result.failed
          .map((f) => `${f.make} ${f.model}`)
          .join(', ');
        toast(t('sc_toast_failed_make_models', { pairs }), 'warning');
      }
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('sc_toast_create_failed'),
        'error'
      );
      throw e;
    }
  };

  const handleSave = async (
    id: string,
    payload: UpdateStatusCodePayload
  ): Promise<void> => {
    try {
      const result = await api.update(id, payload);
      await fetchRecords();
      setEditRecord(null);
      toast(t('sc_toast_updated'), 'success');
      if (result.failed.length > 0) {
        const pairs = result.failed
          .map((f) => `${f.make} ${f.model}`)
          .join(', ');
        toast(t('sc_toast_failed_make_models', { pairs }), 'warning');
      }
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('sc_toast_update_failed'),
        'error'
      );
      throw e;
    }
  };

  const handleDelete = useCallback(
    (id: string, code: number, description: string) => {
      setDeleteTarget({ id, code, description });
    },
    []
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.remove(deleteTarget.id);
      await fetchRecords();
      toast(t('sc_toast_deleted'), 'success');
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('sc_toast_delete_failed'),
        'error'
      );
    } finally {
      setDeleteTarget(null);
    }
  };

  const uniqueStatuses = useMemo(
    () => [...new Set(records.map((r) => r.status).filter(Boolean))].sort(),
    [records]
  );

  const filteredRecords = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return records.filter((r) => {
      if (statusFilter.length > 0 && !statusFilter.includes(r.status))
        return false;
      if (!q) return true;
      const makeModelStr = (r.makeModels ?? [])
        .map((m) => `${m.make} ${m.model}`)
        .join(' ')
        .toLowerCase();
      return (
        String(r.code).includes(q) ||
        r.status?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q) ||
        r.reasonCategory?.toLowerCase().includes(q) ||
        r.colorCode?.toLowerCase().includes(q) ||
        makeModelStr.includes(q)
      );
    });
  }, [records, searchQuery, statusFilter]);

  // reset to page 0 whenever search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const pageCount = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pagedRecords = filteredRecords.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'code',
        headerName: t('sc_col_code'),
        minWidth: 90,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'status',
        headerName: t('sc_col_status'),
        minWidth: 120,
        flex: 1,
      },
      {
        field: 'description',
        headerName: t('sc_col_description'),
        minWidth: 160,
        flex: 1.5,
      },
      {
        field: 'colorCode',
        headerName: t('sc_col_color_code'),
        minWidth: 140,
        renderCell: (params) => {
          const color = params.value as string;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: color,
                  border: '1px solid',
                  borderColor: (th) => alpha(th.palette.divider, 0.5),
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
              >
                {color}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'reasonCategory',
        headerName: t('sc_col_reason_category'),
        minWidth: 160,
        flex: 1.5,
      },
      {
        field: 'makeModels',
        headerName: t('sc_col_make_models'),
        minWidth: 220,
        flex: 2,
        sortable: false,
        renderCell: (params) => {
          const items = (params.value as MakeModelRecord[]) ?? [];
          const MAX_CHIPS = 3;
          const visible = items.slice(0, MAX_CHIPS);
          const extra = items.length - MAX_CHIPS;
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
              {visible.map((m) => (
                <Chip
                  key={m.id}
                  label={`${m.make} ${m.model}`}
                  size="small"
                  sx={{ borderRadius: '6px', fontSize: '0.72rem', height: 22 }}
                />
              ))}
              {extra > 0 && (
                <Chip
                  label={`+${extra}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: '6px',
                    fontSize: '0.72rem',
                    height: 22,
                    color: 'text.secondary',
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: 'actions',
        headerName: t('sc_col_actions'),
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={t('sc_action_edit')}>
              <IconButton
                size="small"
                onClick={() => {
                  setEditRecord(params.row as StatusCodeRecord);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('sc_action_delete')}>
              <IconButton
                size="small"
                onClick={() => {
                  const row = params.row as StatusCodeRecord;
                  handleDelete(row.id, row.code, row.description);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t, handleDelete]
  );

  const hasData = records.length > 0;
  const hasResults = filteredRecords.length > 0;

  return (
    <Box
      sx={{
        height: 'calc(100vh - 115px)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      <AddStatusCodeModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onAdd={handleAdd}
        makeModelOptions={makeModelOptions}
      />
      <EditStatusCodeModal
        record={editRecord}
        onClose={() => {
          setEditRecord(null);
        }}
        onSave={handleSave}
        makeModelOptions={makeModelOptions}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th) => alpha(th.palette.error.main, 0.2),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {t('sc_delete_title')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('sc_delete_body', {
              code: deleteTarget?.code ?? '',
              description: deleteTarget?.description ?? '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setDeleteTarget(null);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('sc_cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              void confirmDelete();
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('sc_delete_confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {t('sc_toolbar_title')}
          </Typography>
          {hasData && (
            <Typography
              variant="caption"
              sx={{
                px: 1.2,
                py: 0.3,
                borderRadius: '20px',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              {searchQuery.trim()
                ? `${filteredRecords.length} / ${records.length}`
                : records.length}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant={statusFilter.length > 0 ? 'contained' : 'outlined'}
            color={statusFilter.length > 0 ? 'primary' : 'inherit'}
            startIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => {
              setFilterAnchor(e.currentTarget);
            }}
            sx={{
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.78rem',
              textTransform: 'none',
            }}
          >
            {statusFilter.length > 0
              ? t('sc_filter_status_active', { count: statusFilter.length })
              : t('sc_filter_status')}
          </Button>
          <Menu
            anchorEl={filterAnchor}
            open={Boolean(filterAnchor)}
            onClose={() => {
              setFilterAnchor(null);
            }}
            PaperProps={{
              sx: { borderRadius: '10px', minWidth: 160, mt: 0.5 },
            }}
          >
            {uniqueStatuses.map((s) => (
              <MenuItem
                key={s}
                dense
                onClick={() => {
                  setStatusFilter((prev) =>
                    prev.includes(s)
                      ? prev.filter((x) => x !== s)
                      : [...prev, s]
                  );
                }}
                sx={{ gap: 0.5 }}
              >
                <Checkbox
                  checked={statusFilter.includes(s)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
                <ListItemText
                  primary={s}
                  primaryTypographyProps={{ fontSize: '0.82rem' }}
                />
              </MenuItem>
            ))}
            {statusFilter.length > 0 && (
              <MenuItem
                dense
                onClick={() => {
                  setStatusFilter([]);
                  setFilterAnchor(null);
                }}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  mt: 0.5,
                  color: 'text.secondary',
                }}
              >
                <ListItemText
                  primary={t('sc_filter_clear')}
                  primaryTypographyProps={{ fontSize: '0.78rem' }}
                />
              </MenuItem>
            )}
          </Menu>
          <TextField
            size="small"
            placeholder={t('sc_search_placeholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                    }}
                    edge="end"
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.82rem',
              },
            }}
          />
          <Tooltip title={t('sc_toolbar_refresh')}>
            <IconButton
              size="small"
              onClick={() => {
                void fetchRecords();
              }}
              disabled={loading}
              sx={{
                color: 'text.secondary',
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setAddOpen(true);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem' }}
          >
            {t('sc_toolbar_add')}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && records.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && !hasData && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                border: '1.5px solid',
                borderColor: (th) => alpha(th.palette.primary.main, 0.3),
                mb: 1,
              }}
            >
              <CodeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {t('sc_empty_title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 300, lineHeight: 1.7, mb: 1 }}
            >
              {t('sc_empty_subtitle')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setAddOpen(true);
              }}
              sx={{ fontWeight: 600, borderRadius: '8px', px: 3 }}
            >
              {t('sc_toolbar_add')}
            </Button>
          </Box>
        )}

        {hasData && !hasResults && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <SearchIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {t('sc_no_results')}
            </Typography>
          </Box>
        )}

        {hasData && hasResults && (
          <>
            <CustomStyledDataGrid
              rows={pagedRecords}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooter
              getRowHeight={() => 48}
              sx={{
                flex: 1,
                overflow: 'auto',
                border: 'none',
                '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                  position: 'sticky',
                  right: 0,
                  bgcolor: 'var(--header-bg-dark)',
                },
                '& .MuiDataGrid-cell[data-field="actions"]': {
                  position: 'sticky',
                  right: 0,
                  bgcolor: 'background.paper',
                },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: (th) => alpha(th.palette.divider, 0.4),
                flexShrink: 0,
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  setPage((p) => Math.max(0, p - 1));
                }}
                disabled={safePage === 0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  p: 0.5,
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 80, textAlign: 'center' }}
              >
                {safePage + 1} / {pageCount}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setPage((p) => Math.min(pageCount - 1, p + 1));
                }}
                disabled={safePage >= pageCount - 1}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  p: 0.5,
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export const StatusCodesView = (): JSX.Element => (
  <CustomMuiThemeProvider>
    <StatusCodesContent />
  </CustomMuiThemeProvider>
);
